import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { adminApi } from "@/lib/admin-api";

/**
 * Gates /admin/* routes.
 *
 * Three states:
 *   1. AuthContext is still loading              → spinner
 *   2. No JWT in storage                          → redirect to /login (preserve location)
 *   3. JWT present but /admin/me says not admin   → redirect to /dashboard
 *   4. /admin/me says isAdmin=true                → render children
 *
 * The /admin/me probe is intentionally separate from AuthContext so the
 * regular user dashboard never has to know about admin status.
 */
export default function AdminGuard({ children }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [probing, setProbing] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return undefined;
    if (!user) {
      setProbing(false);
      return undefined;
    }
    (async () => {
      try {
        const data = await adminApi.me();
        if (!cancelled) setIsAdmin(Boolean(data?.isAdmin));
      } catch {
        if (!cancelled) setIsAdmin(false);
      } finally {
        if (!cancelled) setProbing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user]);

  if (authLoading || (user && probing)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-muted-foreground text-sm">Verifying admin access…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
